import React, { useState } from 'react';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import {
    AgentHypothesesCellRenderValue,
    SimulationAgentData,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import {
    AGENT_SOFTWARE_ID,
    JOB_NUMBER,
    MANTY_STATUS,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import PaymentsIcon from '@mui/icons-material/Payments';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VerifiedIcon from '@mui/icons-material/Verified';
import StyIconChip from 'routes/HumanResourcesModule/components/ui/StyIconChip';
import { MANTY_COLORS, UNMAPPED } from 'business/constants';
import Flex from 'ui/Flex/Flex';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import AgentHypothesisModal from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/AgentHypothesisModal';
import { HypothesisChoice } from '../../AddHypotheses/AgentHypothesisModal/constants';
import AddContent from './AddContent';
import { useAgentPayComponentHypothesisCRUDMutations } from '../../PayComponent/helpers/hooks/agentPayComponentHypothesis';
import { useAgentDepartureHypothesisCRUDMutations } from '../../Departure/helpers/hooks/agentDepartureHypothesis';
import { useGradeAdvancementHypothesisCRUDMutations } from '../../GradeAdvancement/helpers/hooks/gradeAdvancement';
import { useTranslation } from 'react-i18next';
import { AgentDepartureHypothesis } from '../../Departure/helpers/types';
import { GradeAdvancementHypothesis } from '../../GradeAdvancement/helpers/types';
import { AgentPayComponentHypothesis } from '../../PayComponent/helpers/types';

type Props = CellRendererParams<
    SimulationAgentData, // type of params.data
    AgentHypothesesCellRenderValue // type of params.value
> & {
    hrSimulation: HrSimulation;
    recurrence: RecurrenceEnum;
};

const AgentHypothesisCellRenderer = ({
    value,
    data,
    hrSimulation,
    recurrence,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState<boolean>(false);

    const mantyStatus = data?.[MANTY_STATUS];
    const agentSoftwareId = data?.[AGENT_SOFTWARE_ID];
    const jobNumber = data?.[JOB_NUMBER];
    const [selectedHypothesisType, setSelectedHypothesisType] = useState<
        string | null
    >(null);
    const orgChartHrSoftwareId = data?.[ORG_CHART_HR_SOFTWARE_ID];
    const [selectedHypothesisId, setSelectedHypothesisId] = useState<
        number | undefined
    >(undefined);

    const departureHypothesis = value?.agentDepartureHypothesis;
    const gradeAdvancementHypothesis = value?.gradeAdvancementHypothesis;

    const ifseHypothesis = value?.ifsePayComponentHypotheses;
    const ponctualPrimeHypothesis = value?.ponctualPrimesPayComponentHypotheses;
    const {
        createHypothesis: createAgentPayComponentHypothesis,
        editHypothesis: editAgentPayComponentHypothesis,
        deleteHypothesis: deleteAgentPayComponentHypothesis,
        isLoading: isAgentPayComponentHypothesisMutationLoading,
    } = useAgentPayComponentHypothesisCRUDMutations({
        simulationId: hrSimulation.id,
        recurrence,
        mantyStatus,
        agentSoftwareId,
        jobNumber,
        orgChartHrSoftwareId,
        hypothesisId: selectedHypothesisId,
    });
    const {
        createHypothesis: createDepartureHypothesis,
        editHypothesis: editDepartureHypothesis,
        deleteHypothesis: deleteDepartureHypothesis,
        isHypothesisMutationLoading: isDepartureHypothesisMutationLoading,
    } = useAgentDepartureHypothesisCRUDMutations({
        simulationId: hrSimulation.id,
        recurrence,
        mantyStatus,
        agentSoftwareId,
        jobNumber,
        hypothesisId: departureHypothesis?.id,
    });

    const {
        createHypothesis: createGradeAdvancementHypothesis,
        editHypothesis: editGradeAdvancementHypothesis,
        deleteHypothesis: deleteGradeAdvancementHypothesis,
        isLoading: isGradeAdvancementMutationLoading,
    } = useGradeAdvancementHypothesisCRUDMutations({
        simulationId: hrSimulation.id,
        recurrence,
        mantyStatus,
        agentSoftwareId,
        jobNumber,
        orgChartHrSoftwareId,
        hypothesisId: gradeAdvancementHypothesis?.id,
    });

    const isLoading =
        isDepartureHypothesisMutationLoading ||
        isAgentPayComponentHypothesisMutationLoading ||
        isGradeAdvancementMutationLoading;

    const hypotheses: (
        | AgentDepartureHypothesis
        | AgentPayComponentHypothesis
        | GradeAdvancementHypothesis
    )[] = [];
    if (departureHypothesis) hypotheses.push(departureHypothesis);
    if (ifseHypothesis) hypotheses.push(ifseHypothesis);
    if (ponctualPrimeHypothesis) hypotheses.push(ponctualPrimeHypothesis);
    if (gradeAdvancementHypothesis) hypotheses.push(gradeAdvancementHypothesis);

    const setCurrentHypothesisId = () => {
        if (selectedHypothesisType === HypothesisChoice.PRIME) {
            setSelectedHypothesisId(ponctualPrimeHypothesis?.id);
        } else if (selectedHypothesisType === HypothesisChoice.IFSE) {
            setSelectedHypothesisId(ifseHypothesis?.id);
        } else if (selectedHypothesisType === HypothesisChoice.DEPARTURE) {
            setSelectedHypothesisId(departureHypothesis?.id);
        } else if (selectedHypothesisType === HypothesisChoice.GRADE) {
            setSelectedHypothesisId(gradeAdvancementHypothesis?.id);
        }
    };

    return (
        <Flex gap="0.5em" alignItems="center">
            <>
                <AddContent
                    isLoading={isLoading}
                    hypotheses={hypotheses}
                    setShowModal={setShowModal}
                    isDisabled={orgChartHrSoftwareId === UNMAPPED}
                />

                {departureHypothesis && data && (
                    <StyIconChip
                        label=""
                        title={t(
                            'humanResources.simulation.agentHypothesis.departureHypothesis',
                        )}
                        icon={<ExitToAppIcon />}
                        styledColor={MANTY_COLORS.cyan}
                    />
                )}
                {ponctualPrimeHypothesis && data && (
                    <StyIconChip
                        title={t(
                            'humanResources.simulation.agentHypothesis.ponctualPrimesHypotheses',
                        )}
                        icon={<CardGiftcardIcon />}
                        styledColor={MANTY_COLORS.yellow}
                    />
                )}
                {ifseHypothesis && data && (
                    <StyIconChip
                        title={t(
                            'humanResources.simulation.agentHypothesis.ifseHypotheses',
                        )}
                        icon={<PaymentsIcon />}
                        styledColor={MANTY_COLORS.green}
                    />
                )}
                {gradeAdvancementHypothesis && data && (
                    <StyIconChip
                        icon={<VerifiedIcon />}
                        styledColor={MANTY_COLORS.red}
                    />
                )}
                {showModal && data && (
                    <AgentHypothesisModal
                        departureHypothesis={departureHypothesis}
                        ifseHypothesis={ifseHypothesis}
                        selectedHypothesisType={selectedHypothesisType}
                        setSelectedHypothesisType={setSelectedHypothesisType}
                        ponctualPrimeHypothesis={ponctualPrimeHypothesis}
                        setCurrentHypothesisId={setCurrentHypothesisId}
                        show={showModal}
                        onClose={() => setShowModal(false)}
                        hrSimulation={hrSimulation}
                        agentData={data}
                        recurrence={recurrence}
                        gradeAdvancementHypothesis={gradeAdvancementHypothesis}
                        agentDepartureHpothesisCRUDOperations={{
                            createDepartureHypothesis,
                            editDepartureHypothesis,
                            deleteDepartureHypothesis,
                        }}
                        agentPayComponentHpothesisCRUDOperations={{
                            createAgentPayComponentHypothesis,
                            editAgentPayComponentHypothesis,
                            deleteAgentPayComponentHypothesis,
                        }}
                        gradeAdvancementHypothesisCRUDOperations={{
                            createGradeAdvancementHypothesis,
                            editGradeAdvancementHypothesis,
                            deleteGradeAdvancementHypothesis,
                        }}
                    />
                )}
            </>
        </Flex>
    );
};

export default AgentHypothesisCellRenderer;
